import { useMemo } from 'react'
import { Alert, Box, Card, CircularProgress } from '@mui/material'
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type ChartData,
  type ChartOptions,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { usePropertyClimateData } from '../../../../hooks/climateData'
import { CanonicalPeriod, EmissionTier, getPeriodYear, PERIODS } from '../../common'
import ClimateSectionTitle from '../../common/ClimateSectionTitle'
import { getSelectedPeriodPointY, getSucceededMetricData } from '../overviewMetrics'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend)

const chartOptions: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom' },
    tooltip: {
      callbacks: {
        label: context => `${context.dataset.label}: ${context.parsed.y.toFixed(1)} days/year`,
      },
    },
  },
  scales: {
    x: {
      title: { display: true, text: 'Climate horizon' },
    },
    y: {
      beginAtZero: true,
      title: { display: true, text: 'Freeze-thaw days/year' },
    },
  },
}

type FreezeThawTrendsProps = {
  propertyId: number
  emissionTier: EmissionTier
}

export default function FreezeThawTrends({ propertyId, emissionTier }: FreezeThawTrendsProps) {
  const { data: days, isLoading, error } = usePropertyClimateData('freeze_thaw:freeze_thaw_days', { propertyId })
  const payload = getSucceededMetricData(days)

  const chartData = useMemo<ChartData<'line'>>(
    () => ({
      labels: PERIODS.map(period => String(getPeriodYear(period))),
      datasets: [
        {
          label: 'Ensemble mean',
          data: PERIODS.map(period => getSelectedPeriodPointY(payload, emissionTier, period) ?? Number.NaN),
          borderColor: '#0284c7',
          backgroundColor: '#0284c7',
          tension: 0.2,
          pointRadius: 3,
        },
      ],
    }),
    [emissionTier, payload],
  )

  const hasData = PERIODS.some(
    (period: CanonicalPeriod) => getSelectedPeriodPointY(payload, emissionTier, period) != null,
  )
  const showLoading = isLoading && !days && !hasData

  return (
    <Card sx={{ p: 3 }}>
      <Box mb={2}>
        <ClimateSectionTitle
          title="Freeze-Thaw Day Projections"
          subtitle="Projected annual freeze-thaw days across climate horizons"
          helpText="The chart shows ensemble-mean annual freeze-thaw days for the selected emissions scenario. Year labels represent climate horizons rather than exact single-year observations."
          loading={isLoading}
        />
      </Box>

      {showLoading && (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={220}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!showLoading && hasData && (
        <Box sx={{ height: 280 }}>
          <Line data={chartData} options={chartOptions} />
        </Box>
      )}

      {!showLoading && !hasData && (
        <Alert severity={error ? 'error' : 'info'}>
          {error ? 'Unable to load freeze-thaw projections.' : 'No freeze-thaw projections to display.'}
        </Alert>
      )}
    </Card>
  )
}
